import { DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { EducationDetailDto } from "../../types/client-education-detail.types";
import styles from "./sections.module.scss";

function formatSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExtension(name?: string): string {
  if (!name) return "";
  const idx = name.lastIndexOf(".");
  if (idx < 0 || idx === name.length - 1) return "";
  return name.slice(idx + 1).toLowerCase();
}

export function ResourcesSection(props: { education: EducationDetailDto }) {
  const { t } = useTranslation();
  const files = props.education.documentFiles ?? [];
  if (!files.length) return null;
  return (
    <section id="resources" className={styles.section}>
      <div className={styles.header}>
        <h2>{t("resourcesSection.title")}</h2>
        <span className={styles.headerCount}>
          {t("resourcesSection.fileCount", { count: files.length })}
        </span>
      </div>
      <div className={styles.resources}>
        {files.map((f) => {
          const ext = getExtension(f.originalName);
          const sizeText = formatSize(f.size);
          return (
            <div key={f.id} className={styles.resourceRow}>
              <div className={styles.resourceLeft}>
                <span className={styles.fileBadge} data-ext={ext} aria-hidden>
                  {ext ? ext.toUpperCase().slice(0, 4) : "FILE"}
                </span>
                <div className={styles.resourceInfo}>
                  <span className={styles.resourceName}>
                    {f.originalName ?? t("resourcesSection.fileFallback")}
                  </span>
                  {(ext || sizeText) && (
                    <span className={styles.resourceMeta}>
                      {ext && <span>{ext.toUpperCase()}</span>}
                      {ext && sizeText && (
                        <span className={styles.resourceMetaDot} />
                      )}
                      {sizeText && <span>{sizeText}</span>}
                    </span>
                  )}
                </div>
              </div>
              {f.downloadUrl ? (
                <a
                  href={f.downloadUrl}
                  download
                  className={styles.downloadButton}
                >
                  <DownloadOutlined /> {t("resourcesSection.download")}
                </a>
              ) : (
                <span
                  className={styles.downloadDisabled}
                  title={t("resourcesSection.downloadDisabledHint")}
                >
                  —
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
